import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, X, Send, Loader2, Sparkles, Mic, MicOff } from "lucide-react";
import { api } from "@/lib/api";
import { SpeakButton } from "@/components/SpeakButton";
import { toast } from "sonner";

// Web Speech API — lazily create per session; ChromE/Edge/Safari support the SpeechRecognition
// name via webkitSpeechRecognition. Firefox has no support — we hide the mic button there.
const SpeechRecognition =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;
const SPEECH_SUPPORTED = !!SpeechRecognition;

export const AskProfessor = ({ lecture, readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(() => localStorage.getItem("lectura_autospeak") === "1");
  const scrollRef = useRef(null);
  const recRef = useRef(null);
  const lastReplyIdxRef = useRef(-1);

  const suggestions = (lecture.key_concepts || [])
    .slice(0, 3)
    .map((c) => `Explain "${c.concept}" simply`);

  useEffect(() => {
    setMessages([]);
  }, [lecture?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Clean up any active speech recognizer on close/unmount
  useEffect(() => {
    return () => {
      try { recRef.current?.stop(); } catch {}
    };
  }, []);

  const toggleMic = () => {
    if (!SPEECH_SUPPORTED) {
      toast.error("Voice input isn't supported in this browser. Try Chrome, Edge or Safari.");
      return;
    }
    if (listening) {
      try { recRef.current?.stop(); } catch {}
      return;
    }
    try {
      const rec = new SpeechRecognition();
      rec.lang = navigator.language || "en-US";
      rec.interimResults = true;
      rec.continuous = false;
      let finalText = "";
      rec.onstart = () => setListening(true);
      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) finalText += t;
          else interim += t;
        }
        setInput((prev) => {
          const base = (prev || "").replace(/\s+$/, "");
          const glue = base && !finalText.startsWith(" ") ? " " : "";
          return (finalText ? base + glue + finalText : base + " " + interim).trim();
        });
      };
      rec.onerror = (e) => {
        setListening(false);
        if (e?.error !== "aborted" && e?.error !== "no-speech") {
          toast.error("Voice input failed — try again.");
        }
      };
      rec.onend = () => {
        setListening(false);
      };
      recRef.current = rec;
      rec.start();
    } catch (e) {
      toast.error("Couldn't start voice input.");
      setListening(false);
    }
  };

  const toggleAutoSpeak = () => {
    const next = !autoSpeak;
    setAutoSpeak(next);
    localStorage.setItem("lectura_autospeak", next ? "1" : "0");
    toast.message(next ? "Auto-speak ON — I'll read replies aloud." : "Auto-speak OFF");
  };

  if (readOnly) return null;

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || loading) return;
    if (listening) { try { recRef.current?.stop(); } catch {} }
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: question }]);
    setInput("");
    setLoading(true);
    try {
      const res = await api.post(`/lectures/${lecture.id}/ask`, { question, history });
      setMessages((m) => {
        const nextIdx = m.length;
        lastReplyIdxRef.current = nextIdx;
        return [...m, { role: "assistant", content: res.data.answer, autoSpeak }];
      });
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't answer that just now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="ask-professor-fab"
        onClick={() => setOpen(true)}
        initial={{ scale: 0 }}
        animate={{ scale: open ? 0 : 1 }}
        whileHover={{ scale: 1.05 }}
        className="no-print fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-primary text-primary-foreground pl-4 pr-5 py-3 shadow-xl"
      >
        <GraduationCap className="h-5 w-5" />
        <span className="font-semibold text-sm">Ask the Professor</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="no-print fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[80vh] rounded-3xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-primary text-primary-foreground">
              <div className="flex items-center gap-2.5">
                <span className="grid place-items-center h-9 w-9 rounded-full bg-primary-foreground/15">
                  <GraduationCap className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-serif text-lg font-semibold leading-none">The Professor</p>
                  <p className="text-xs opacity-70 mt-0.5">Tutor for this chapter</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="ask-professor-autospeak"
                  onClick={toggleAutoSpeak}
                  title={autoSpeak ? "Auto-speak ON — replies read aloud" : "Auto-speak OFF"}
                  className={`grid place-items-center h-8 w-8 rounded-full transition ${
                    autoSpeak ? "bg-accent text-accent-foreground" : "bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/25"
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid="ask-professor-close"
                  onClick={() => setOpen(false)}
                  className="grid place-items-center h-8 w-8 rounded-full hover:bg-primary-foreground/15 transition"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto custom-scroll px-4 py-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center pt-6">
                  <span className="grid place-items-center h-14 w-14 mx-auto rounded-2xl bg-secondary text-accent">
                    <Sparkles className="h-6 w-6" />
                  </span>
                  <p className="mt-4 font-serif text-xl font-semibold">Hi! Ask me anything</p>
                  <p className="mt-1 text-sm text-muted-foreground px-4">
                    I know this chapter inside out. Curious about a concept? Just ask.
                  </p>
                  <div className="mt-5 space-y-2">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        data-testid={`ask-suggestion-${i}`}
                        onClick={() => send(s)}
                        className="w-full text-left rounded-xl border border-border px-4 py-2.5 text-sm hover:border-primary/50 hover:bg-secondary/50 transition"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${m.role === "assistant" ? "space-y-1.5" : ""}`}>
                    <div
                      data-testid={`chat-msg-${m.role}`}
                      className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-accent text-accent-foreground rounded-br-sm"
                          : "bg-secondary text-foreground rounded-bl-sm"
                      }`}
                    >
                      {m.content}
                    </div>
                    {m.role === "assistant" && (
                      <SpeakButton text={m.content} testid={`chat-speak-${i}`} label="Listen" autoPlay={!!m.autoSpeak} />
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-secondary rounded-2xl rounded-bl-sm px-4 py-3">
                    <span className="flex gap-1">
                      {[0, 1, 2].map((d) => (
                        <motion.span
                          key={d}
                          className="h-2 w-2 rounded-full bg-muted-foreground"
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                        />
                      ))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-border flex items-center gap-2"
            >
              {SPEECH_SUPPORTED && (
                <button
                  type="button"
                  data-testid="ask-professor-mic"
                  onClick={toggleMic}
                  title={listening ? "Stop recording" : "Speak your question"}
                  className={`grid place-items-center h-10 w-10 rounded-full transition ${
                    listening
                      ? "bg-destructive text-destructive-foreground animate-pulse"
                      : "bg-secondary text-muted-foreground hover:text-foreground hover:bg-accent/10"
                  }`}
                >
                  {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>
              )}
              <input
                data-testid="ask-professor-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={listening ? "Listening…" : "Ask about this chapter…"}
                className="flex-1 rounded-full border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/60"
              />
              <button
                data-testid="ask-professor-send"
                type="submit"
                disabled={loading || !input.trim()}
                className="grid place-items-center h-10 w-10 rounded-full bg-primary text-primary-foreground disabled:opacity-40 hover:opacity-90 transition"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
